import { useCallback, useEffect, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import AIButton from 'ai-components-ui-library/components/buttons/AIButton';
import AILoadingButton from 'ai-components-ui-library/components/buttons/AILoadingButton';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { fetchLatestPreview } from '../../../api/ChatSpotV1HighVolumeApi';
import { useStreamingAction } from '../../../hooks/useStreamingAction';
import CopilotPreviewInner from './CopilotPreviewInner';
import BreezeTile from './breeze/BreezeTile';
import { toEventResponse } from './utils/LatestPreviewApi';
import { useLocalSuperstore } from '../../../hooks/useLocalSuperstore';
import { getIsPastRefreshThreshold } from './utils/RefreshButton';
import { useIsDisabledByAccessControls } from './hooks/useIsDisabledByAccessControls';
import { DisabledPreviewControls } from './DisabledPreviewControls';
import { useShouldShowActions } from '../../../hooks/useShouldShowActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const COPILOT_PREVIEW_CLOSED_STORAGE_KEY = 'COPILOT_PREVIEW_CLOSED';
const CopilotPreviewActionButton = ({
  actionButtonRef,
  actionPayload,
  aiTracker,
  autoGenerate,
  copilotAction,
  children,
  copilotActionButtonText,
  hasAIBadge,
  previewKey,
  previewSource,
  onClick,
  onClose,
  previewTitle,
  showCloseButton,
  numHoursTilSummaryStaleRefresh,
  onChatWithCopilot,
  onPositiveFeedback,
  onNegativeFeedback,
  onRefresh,
  onPreviewShown,
  onQuickActionClick,
  usageTrackerAppLocationKey,
  actionButtonLeftHeader,
  overrideDisabled,
  onDontShowAgain
}) => {
  const previewKeyRef = useRef('');
  const [latestPreviewLoading, setLatestPreviewLoading] = useState(false);
  const [latestPreview, setLatestPreview] = useState();
  const {
    sendAction,
    actionResponse,
    isStreaming,
    onReset
  } = useStreamingAction();
  const {
    localSuperstoreValue,
    localSuperstoreChecked,
    handleSetLocalSuperstoreValue
  } = useLocalSuperstore({
    key: `${COPILOT_PREVIEW_CLOSED_STORAGE_KEY}_${usageTrackerAppLocationKey}`
  });
  const {
    isDisabledByAccessControls,
    isAccessRequestLoading
  } = useIsDisabledByAccessControls();
  const shouldShowActions = useShouldShowActions();
  const isDisabled = (overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.isDisabled) || isDisabledByAccessControls;
  const hasPreviewKeyChanged = previewKey && previewKeyRef.current !== previewKey;
  useEffect(() => {
    if (hasPreviewKeyChanged && localSuperstoreChecked && localSuperstoreValue === 'false' && !isAccessRequestLoading) {
      previewKeyRef.current = previewKey;
      setLatestPreview(undefined);
      setLatestPreviewLoading(true);
      fetchLatestPreview({
        previewSource,
        previewKey,
        isAutoGenerated: true
      }).then(response => {
        /** If the preview is stale we will regenerate by firing another action */

        if (response.isStale && !isDisabled) {
          sendAction(actionPayload);
        } else {
          const latestPreviewData = toEventResponse(response);

          // this is to refresh the preview if it has been 1 hour since it was last generated
          const isStaleForRefresh = getIsPastRefreshThreshold({
            date: latestPreviewData.timestamp,
            threshold: numHoursTilSummaryStaleRefresh
          });
          if (isStaleForRefresh && !isDisabled) {
            sendAction(actionPayload);
          } else {
            setLatestPreview(latestPreviewData);
          }
        }
        setLatestPreviewLoading(false);
      }).catch(err => {
        setLatestPreviewLoading(false);
        /** When enabled this will check if latest preview exists
         * If not it will fire the action without a user needing
         * to click the button
         */
        if (autoGenerate && err.status === 404 && !isDisabled) {
          sendAction(actionPayload);
        }
      });
    }
  }, [previewKey, previewSource, hasPreviewKeyChanged, autoGenerate, localSuperstoreValue, localSuperstoreChecked, sendAction, actionPayload, onReset, numHoursTilSummaryStaleRefresh, isDisabled, isAccessRequestLoading]);
  const handleReset = useCallback(() => {
    setLatestPreview(undefined);
    onReset();
  }, [onReset]);
  const handleClose = useCallback(() => {
    handleSetLocalSuperstoreValue('true');
    if (onClose) {
      onClose();
    }
    handleReset();
    aiTracker.track('Ai Addon Copilot Preview', 'Copilot Preview Interaction', {
      action: 'close_preview'
    });
  }, [handleSetLocalSuperstoreValue, onClose, handleReset, aiTracker]);
  const handleClick = useCallback(() => {
    handleSetLocalSuperstoreValue('false');
    if (onClick) {
      onClick();
    }
    aiTracker.track('Ai Addon Copilot Preview', 'Copilot Preview Interaction', {
      action: 'primary_action_click',
      command: JSON.stringify(copilotAction)
    });

    /** We need to handle when a user clicks on the button
     * If lastet preview we return to it can be rendered
     * If not we try and refetch the preview to ensure its not present
     * If none of the above are true we send the action to get a new preview
     */
    if (previewKey) {
      setLatestPreview(undefined);
      setLatestPreviewLoading(true);
      fetchLatestPreview({
        previewSource,
        previewKey,
        isAutoGenerated: false
      }).then(response => {
        /** If the preview is stale we will regenerate by firing another action */
        if (response.isStale) {
          sendAction(actionPayload);
        } else {
          const latestPreviewData = toEventResponse(response);
          setLatestPreview(latestPreviewData);
        }
        setLatestPreviewLoading(false);
      }).catch(err => {
        setLatestPreviewLoading(false);
        if (err.status === 404) {
          const payload = {
            data: Object.assign({}, actionPayload.data, {
              chatPreview: Object.assign({}, actionPayload.data.chatPreview, {
                isAutoGenerated: false
              })
            })
          };
          sendAction(payload);
        }
      });
    }
  }, [actionPayload, aiTracker, copilotAction, handleSetLocalSuperstoreValue, onClick, previewKey, previewSource, sendAction]);
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    }
    setLatestPreview(undefined);
    onReset();
    aiTracker.track('Ai Addon Copilot Preview', 'Copilot Preview Interaction', {
      action: 'refresh_summary'
    });
    const payload = {
      data: Object.assign({}, actionPayload.data, {
        chatPreview: Object.assign({}, actionPayload.data.chatPreview, {
          isAutoGenerated: false
        })
      }),
      isRefresh: true
    };
    sendAction(payload);
  };
  useEffect(() => {
    const activeResponse = latestPreview || actionResponse;
    if (onPreviewShown && activeResponse) {
      onPreviewShown(activeResponse);
    }
  }, [actionResponse, latestPreview, onPreviewShown]);
  const loading = latestPreviewLoading || isStreaming || isAccessRequestLoading;
  if (!actionResponse && !latestPreview && !isDisabled) {
    const loadingButton = /*#__PURE__*/_jsx(AILoadingButton, {
      buttonRef: actionButtonRef,
      loading: loading,
      onClick: handleClick,
      size: "xs",
      usageTracker: aiTracker,
      use: "tertiary",
      children: copilotActionButtonText || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "aiAddonCopilotPreview.components.copilotPreview.buttonTitle"
      })
    });
    if (actionButtonLeftHeader) {
      return /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        children: [actionButtonLeftHeader, loadingButton]
      });
    }
    return loadingButton;
  }
  const activeResponse = latestPreview || actionResponse;
  const renderActions = () => {
    if (isDisabled) {
      return /*#__PURE__*/_jsx(DisabledPreviewControls, {
        usageTracker: aiTracker,
        onDontShowAgain: onDontShowAgain,
        isDisabledDescriptionOverriden: Boolean(overrideDisabled === null || overrideDisabled === void 0 ? void 0 : overrideDisabled.disabledDescription)
      });
    }
    return /*#__PURE__*/_jsxs(View, {
      marginBlockStart: "400",
      children: [showCloseButton && /*#__PURE__*/_jsx(AIButton, {
        disabled: loading,
        onClick: handleClose,
        showBreezeIcon: false,
        size: "xs",
        use: "tertiary",
        usageTracker: aiTracker,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiAddonCopilotPreview.components.copilotPreview.closeButton"
        })
      }), shouldShowActions && /*#__PURE__*/_jsx(AIButton, {
        disabled: loading || !activeResponse,
        onClick: () => {
          if (onChatWithCopilot) {
            onChatWithCopilot(activeResponse);
          }
        },
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiAddonCopilotPreview.components.copilotPreview.chatWithCopilotButton.tooltip"
        }),
        size: "xs",
        use: "secondary",
        usageTracker: aiTracker,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiAddonCopilotPreview.components.copilotPreview.chatWithCopilotButton.message"
        })
      })]
    });
  };
  return /*#__PURE__*/_jsxs(BreezeTile, {
    children: [/*#__PURE__*/_jsx(CopilotPreviewInner, {
      hasAIBadge: hasAIBadge,
      previewTitle: previewTitle,
      onRefresh: handleRefresh,
      onPositiveFeedback: onPositiveFeedback,
      onNegativeFeedback: onNegativeFeedback,
      onQuickActionClick: onQuickActionClick,
      response: activeResponse,
      usageTracker: aiTracker,
      usageTrackerAppLocationKey: usageTrackerAppLocationKey,
      overrideDisabled: overrideDisabled,
      isLoading: loading,
      isStreaming: isStreaming,
      children: children
    }), renderActions()]
  });
};
export default CopilotPreviewActionButton;